import React, { createContext, useContext, useReducer, useEffect } from 'react';
import * as Types from '../services/api/api.types';
import logger from '../utils/logger';

type CartAction =
    | { type: 'ADD_ITEM'; payload: Types.CartItem }
    | { type: 'REMOVE_ITEM'; payload: { key: string } }
    | { type: 'UPDATE_QUANTITY'; payload: { key: string; quantity: number } }
    | { type: 'CLEAR_CART' };

/**
 * Identity of a cart line. A product id alone is NOT unique: the three supplier
 * catalogues share one numeric range, so Dekada 7363 (a PC case) and Vali 7363
 * (a CD-R pack) are different products. Lines are keyed by supplier and id
 * together, and PC configurations by their own id.
 */
export const cartLineKey = (item: Pick<Types.CartItem, 'id' | 'supplier' | 'pcConfigId'>): string =>
    item.pcConfigId != null
        ? `config:${item.pcConfigId}`
        : `${item.supplier ?? 'vali'}:${item.id}`;

const calculateTotal = (items: Types.CartItem[]): number => {
    return items.reduce((total, item) => {
        const price = item.pricePromo !== null ? item.pricePromo : item.price;
        return total + price * item.quantity;
    }, 0);
};

const cartReducer = (state: Types.CartState, action: CartAction): Types.CartState => {
    switch (action.type) {
        case 'ADD_ITEM': {
            const payloadKey = cartLineKey(action.payload);
            const existingItemIndex = state.items.findIndex(item => cartLineKey(item) === payloadKey);

            if (existingItemIndex >= 0) {
                const updatedItems = [...state.items];
                updatedItems[existingItemIndex] = {
                    ...updatedItems[existingItemIndex],
                    quantity: updatedItems[existingItemIndex].quantity + action.payload.quantity
                };

                return {
                    ...state,
                    items: updatedItems,
                    total: calculateTotal(updatedItems)
                };
            } else {
                const updatedItems = [...state.items, action.payload];

                return {
                    ...state,
                    items: updatedItems,
                    total: calculateTotal(updatedItems)
                };
            }
        }

        case 'REMOVE_ITEM': {
            const updatedItems = state.items.filter(item => cartLineKey(item) !== action.payload.key);

            return {
                ...state,
                items: updatedItems,
                total: calculateTotal(updatedItems)
            };
        }

        case 'UPDATE_QUANTITY': {
            const updatedItems = state.items.map(item =>
                cartLineKey(item) === action.payload.key
                    ? { ...item, quantity: action.payload.quantity }
                    : item
            );

            return {
                ...state,
                items: updatedItems,
                total: calculateTotal(updatedItems)
            };
        }

        case 'CLEAR_CART':
            return {
                items: [],
                total: 0
            };

        default:
            return state;
    }
};

const initialCartState: Types.CartState = {
    items: [],
    total: 0
};

interface CartContextType {
    cart: Types.CartState;
    addToCart: (item: Types.CartItem) => void;
    removeFromCart: (key: string) => void;
    updateQuantity: (key: string, quantity: number) => void;
    clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const getInitialState = (): Types.CartState => {
        try {
            const savedCart = localStorage.getItem('cart');
            if (savedCart) {
                const parsedCart = JSON.parse(savedCart);
                // Carts saved before supplier existed hold bare ids, and every
                // one of them came from the Vali catalogue. Backfill on load so
                // a cart left open across the deploy keeps resolving correctly.
                const items: Types.CartItem[] = (parsedCart.items || []).map(
                    (item: Types.CartItem) => (
                        item.supplier || item.pcConfigId != null
                            ? item
                            : { ...item, supplier: 'vali' as Types.ProductSupplier }
                    )
                );
                return {
                    items,
                    total: calculateTotal(items)
                };
            }
        } catch (error) {
            logger.error('Грешка при зареждане на количката от localStorage:', error);
        }

        return initialCartState;
    };

    const [cart, dispatch] = useReducer(cartReducer, getInitialState());

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cart));
    }, [cart]);

    const addToCart = (item: Types.CartItem) => {
        dispatch({ type: 'ADD_ITEM', payload: item });
    };

    const removeFromCart = (key: string) => {
        dispatch({ type: 'REMOVE_ITEM', payload: { key } });
    };

    const updateQuantity = (key: string, quantity: number) => {
        dispatch({ type: 'UPDATE_QUANTITY', payload: { key, quantity } });
    };

    const clearCart = () => {
        dispatch({ type: 'CLEAR_CART' });
    };

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);

    if (context === undefined) {
        throw new Error('useCart трябва да се използва в CartProvider');
    }

    return context;
};